import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fuzzySearchList } from '../lib/fuzzySearch';
import {
  bulkCollectionAction,
  createCollectionPreset,
  deleteCollectionPreset,
  getCollectionActivities,
  getCollectionActivityFeed,
  getCollectionPresets,
  getCollectionsDashboard,
  logCollectionActivity,
  recalculateCollectionKpis,
  searchRivhitCustomers,
  updateCollectionPreset,
  type BulkCollectionActionResponse,
  type CollectionActivity,
  type CollectionActivityFeedItem,
  type CollectionCustomerSummary,
  type CollectionDashboardResponse,
  type CollectionPreset,
  type CollectionPresetFilters,
  type CollectionPresetPurchaseTrend,
  type CollectionTrendByUserPoint,
  type DebtTrendByUserPoint,
  type UserDebtSummary,
} from '../api/client';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import EditCustomerModal from '../components/EditCustomerModal';
import OpenBalanceWhatsAppModal from '../components/OpenBalanceWhatsAppModal';
import { useAuth } from '../context/AuthContext';
import { useRivhitData, type RivhitCustomer } from '../context/RivhitDataContext';

/* ── InfoTooltip ─────────────────────────────────────────────────────── */

function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLSpanElement>(null);

  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
    }
    setShow(true);
  };

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] font-bold cursor-help leading-none flex-shrink-0 hover:bg-gray-300 transition-colors"
      >
        i
      </span>
      {show &&
        createPortal(
          <div
            style={{ top: pos.top, left: pos.left, transform: 'translate(-50%, -100%)' }}
            className="fixed z-[9999] bg-gray-900 text-white text-xs rounded-lg py-2 px-3 max-w-xs shadow-lg pointer-events-none"
          >
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>,
          document.body,
        )}
    </>
  );
}

/* ── helpers ─────────────────────────────────────────────────────────── */

function formatNIS(amount: number) {
  return `₪${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type CollectionPresetDraft = {
  name: string;
  agedDebtGreaterThan: string;
  minOpenBalance: string;
  minCollectionScore: string;
  purchaseTrend: '' | CollectionPresetPurchaseTrend;
  assignment: 'all' | '__unassigned__' | string;
};

const emptyPresetDraft: CollectionPresetDraft = {
  name: '',
  agedDebtGreaterThan: '',
  minOpenBalance: '',
  minCollectionScore: '',
  purchaseTrend: '',
  assignment: 'all',
};

function presetFiltersFromDraft(draft: CollectionPresetDraft): CollectionPresetFilters {
  return {
    aged_debt_greater_than: draft.agedDebtGreaterThan === '' ? null : Number(draft.agedDebtGreaterThan),
    min_open_balance: draft.minOpenBalance === '' ? null : Number(draft.minOpenBalance),
    min_collection_score: draft.minCollectionScore === '' ? null : Number(draft.minCollectionScore),
    purchase_trend: draft.purchaseTrend || null,
    assigned_member_id: draft.assignment !== 'all' && draft.assignment !== '__unassigned__' ? draft.assignment : null,
    unassigned_only: draft.assignment === '__unassigned__',
  };
}

function draftFromPreset(preset: CollectionPreset): CollectionPresetDraft {
  const filters = preset.filters;
  return {
    name: preset.name,
    agedDebtGreaterThan: filters.aged_debt_greater_than == null ? '' : String(filters.aged_debt_greater_than),
    minOpenBalance: filters.min_open_balance == null ? '' : String(filters.min_open_balance),
    minCollectionScore: filters.min_collection_score == null ? '' : String(filters.min_collection_score),
    purchaseTrend: filters.purchase_trend || '',
    assignment: filters.unassigned_only ? '__unassigned__' : (filters.assigned_member_id || 'all'),
  };
}

function scoreColor(score: number) {
  if (score >= 70) return 'bg-red-100 text-red-800';
  if (score >= 40) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
}

function priorityTagKey(score: number) {
  if (score >= 70) return 'collections.priorityTagImmediate';
  if (score >= 40) return 'collections.priorityTagFollowUp';
  return 'collections.priorityTagMonitor';
}

function memberLabel(
  userId: string,
  name: string | null | undefined,
  email: string | null | undefined,
  t: (k: string, o?: Record<string, unknown>) => string,
) {
  return userId === '__unassigned__' ? t('collections.unassigned') : (name || email || userId);
}

type MemberChartSeries = {
  user_id: string;
  label: string;
  color?: string;
};

function MemberChartSelector({
  series,
  selectedUserId,
  onSelect,
  t,
}: {
  series: MemberChartSeries[];
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  if (series.length <= 1) return null;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-gray-400">{t('collections.chartSelectMember')}</span>
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`rounded-full border px-2 py-1 transition-colors ${
          selectedUserId == null
            ? 'border-gray-900 bg-gray-900 text-white'
            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        {t('collections.allMembers')}
      </button>
      {series.map((s) => {
        const selected = selectedUserId === s.user_id;
        return (
          <button
            key={s.user_id}
            type="button"
            onClick={() => onSelect(selected ? null : s.user_id)}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 transition-colors ${
              selected
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {s.color && (
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: s.color }}
              />
            )}
            {s.label}
          </button>
        );
      })}
    </div>
  );
}


const ACTIVITY_ICONS: Record<string, string> = {
  whatsapp_sent: '📱',
  sms_sent: '💬',
  payment_link_created: '💳',
  payment_link_sent: '📨',
  payment_flow_started: '🔄',
  payment_received: '✅',
  reminder_created: '⏰',
  manual_contact: '🤝',
  phone_call: '📞',
  note: '📝',
};

function activityOutcome(activity: CollectionActivity) {
  const details = activity.details || {};
  const raw = [details.outcome, details.status, details.delivery_status]
    .find((value) => typeof value === 'string');
  if (typeof raw !== 'string') return null;
  const normalized = raw.toLowerCase();
  if (['failed', 'error', 'undelivered', 'rejected'].includes(normalized)) return 'failed';
  if (['sent', 'success', 'completed', 'paid', 'delivered', 'queued'].includes(normalized)) return 'success';
  return null;
}

/* ── KPI Card ────────────────────────────────────────────────────────── */

function KPICard({
  label,
  value,
  sub,
  unavailable,
  description,
}: {
  label: string;
  value: string;
  sub?: string;
  unavailable?: boolean;
  description?: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <p className="text-sm text-gray-500 mb-1 flex items-center gap-1">
        {label}
        {description && <InfoTooltip text={description} />}
      </p>
      <p className={`text-2xl font-bold ${unavailable ? 'text-gray-400' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

/* ── Debt by User Table ──────────────────────────────────────────────── */

function DebtByUserTable({
  users,
  t,
}: {
  users: UserDebtSummary[];
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  if (!users.length) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-gray-700">{t('collections.debtByUser')}</h2>
        <InfoTooltip text={t('collections.tooltip_debtByUser')} />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
            <th className="p-3 text-start">{t('collections.teamMember')}</th>
            <th className="p-3 text-end">{t('collections.openBalance')}</th>
            <th className="p-3 text-end">{t('collections.agedDebtAmount')}</th>
            <th className="p-3 text-end">{t('collections.collectedThisMonth')}</th>
            <th className="p-3 text-end">{t('collections.assignedCustomers')}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-b hover:bg-gray-50 transition-colors">
              <td className="p-3">
                <div className="font-medium text-gray-800">
                  {u.user_id === '__unassigned__' ? t('collections.unassigned') : (u.name || u.email || u.user_id)}
                </div>
                {u.name && u.email && <div className="text-xs text-gray-400">{u.email}</div>}
              </td>
              <td className="p-3 text-end font-medium">{formatNIS(u.total_open_debt)}</td>
              <td className="p-3 text-end">
                {u.aged_debt > 0 ? (
                  <span className="text-red-600 font-medium">{formatNIS(u.aged_debt)}</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="p-3 text-end">
                {u.paid_this_month != null ? (
                  <span className="text-green-600 font-medium">{formatNIS(u.paid_this_month)}</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="p-3 text-end text-gray-600">{u.customer_count}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {(() => {
            const totalPaid = users.every((u) => u.paid_this_month == null)
              ? null
              : users.reduce((s, u) => s + (u.paid_this_month ?? 0), 0);
            return (
              <tr className="bg-gray-50 font-semibold text-gray-700 border-t">
                <td className="p-3">{t('collections.total')}</td>
                <td className="p-3 text-end">{formatNIS(users.reduce((s, u) => s + u.total_open_debt, 0))}</td>
                <td className="p-3 text-end">
                  {users.reduce((s, u) => s + u.aged_debt, 0) > 0 ? (
                    <span className="text-red-600">{formatNIS(users.reduce((s, u) => s + u.aged_debt, 0))}</span>
                  ) : '—'}
                </td>
                <td className="p-3 text-end">
                  {totalPaid != null ? (
                    <span className="text-green-600">{formatNIS(totalPaid)}</span>
                  ) : '—'}
                </td>
                <td className="p-3 text-end">{users.reduce((s, u) => s + u.customer_count, 0)}</td>
              </tr>
            );
          })()}
        </tfoot>
      </table>
    </div>
  );
}

/* ── Activity Timeline ───────────────────────────────────────────────── */

function ActivityTimeline({
  customerId,
  refreshVersion,
  t,
}: {
  customerId: number;
  refreshVersion: number;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const [activities, setActivities] = useState<CollectionActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getCollectionActivities(customerId)
      .then(setActivities)
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [customerId, refreshVersion]);

  if (loading) return <p className="text-sm text-gray-400 py-2">{t('collections.loadingActivities')}</p>;
  if (!activities.length) return <p className="text-sm text-gray-400 py-2">{t('collections.noActivities')}</p>;

  return (
    <div className="space-y-2 py-2">
      {activities.map((a) => (
        <div key={a.activity_id} className="flex items-start gap-2 text-sm">
          <span>{ACTIVITY_ICONS[a.activity_type] || '📋'}</span>
          <div>
            <span className="font-medium">{t(`collections.${a.activity_type}`)}</span>
            <span className="text-gray-400 ltr:ml-2 rtl:mr-2">
              {new Date(a.timestamp).toLocaleDateString()}
            </span>
            {a.created_by && (
              <span className="text-gray-400 ltr:ml-1 rtl:mr-1">· {a.created_by}</span>
            )}
            {a.comment && (
              <p
                data-testid={`collection-activity-comment-${a.activity_id}`}
                className="mt-1 whitespace-pre-wrap break-words text-gray-600"
              >
                {a.comment}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Tenant Activity Feed ───────────────────────────────────────────── */

function CollectionActionsFeed({
  refreshVersion,
  t,
}: {
  refreshVersion: number;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activities, setActivities] = useState<CollectionActivityFeedItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [debouncedCustomerQuery, setDebouncedCustomerQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedCustomerQuery(customerQuery);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [customerQuery]);

  const filteredActivities = useMemo(
    () => fuzzySearchList(activities, debouncedCustomerQuery, (a) => [a.customer_name]),
    [activities, debouncedCustomerQuery],
  );

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getCollectionActivityFeed(20);
      setActivities(response.items);
      setNextCursor(response.next_cursor || null);
      setLoaded(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loaded) void loadFirstPage();
    // Refresh only after a feed has been loaded; do not defeat lazy loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshVersion]);

  const toggleExpanded = () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    if (nextExpanded && !loaded && !loading) void loadFirstPage();
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError(false);
    try {
      const response = await getCollectionActivityFeed(20, nextCursor);
      setActivities((current) => {
        const byId = new Map(current.map((activity) => [activity.activity_id, activity]));
        response.items.forEach((activity) => byId.set(activity.activity_id, activity));
        return Array.from(byId.values());
      });
      setNextCursor(response.next_cursor || null);
    } catch {
      setError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section
      data-testid="collection-actions-section"
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
    >
      <button
        type="button"
        data-testid="collection-actions-toggle"
        aria-expanded={expanded}
        aria-controls="collection-actions-content"
        onClick={toggleExpanded}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-start text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <span>{t('collections.actionsFeed')}</span>
        <svg
          aria-hidden="true"
          className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div
          id="collection-actions-content"
          data-testid="collection-actions-content"
          className="border-t border-gray-100"
        >
          {!loading && (activities.length > 0 || customerQuery) && (
            <div className="px-5 py-3 border-b border-gray-100">
              <input
                type="text"
                data-testid="collection-actions-search"
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
                placeholder={t('collections.searchActions')}
                className="w-full max-w-sm rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          )}
          {loading && (
            <p className="p-5 text-sm text-gray-500">{t('collections.loadingActivities')}</p>
          )}
          {!loading && error && !activities.length && (
            <div role="alert" className="p-5 text-sm text-red-600">
              <p>{t('collections.actionsLoadError')}</p>
              <button type="button" onClick={() => void loadFirstPage()} className="mt-2 font-medium underline">
                {t('common.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          )}
          {!loading && !error && !activities.length && (
            <p className="p-5 text-sm text-gray-500">{t('collections.noActivities')}</p>
          )}
          {!loading && activities.length > 0 && filteredActivities.length === 0 && (
            <p className="p-5 text-sm text-gray-500" data-testid="collection-actions-no-results">
              {t('collections.noActionsMatch')}
            </p>
          )}
          {filteredActivities.length > 0 && (
            <div className="divide-y divide-gray-100">
              {filteredActivities.map((activity) => {
                const outcome = activityOutcome(activity);
                const amount = typeof activity.details?.amount === 'number' ? activity.details.amount : null;
                const channel = typeof activity.details?.channel === 'string' ? activity.details.channel : null;
                return (
                  <article
                    key={activity.activity_id}
                    data-testid={`collection-action-${activity.activity_id}`}
                    className="flex items-start gap-3 px-5 py-3"
                  >
                    <span aria-hidden="true" className="mt-0.5 text-lg">
                      {ACTIVITY_ICONS[activity.activity_type] || '📋'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium text-gray-900">{activity.customer_name}</span>
                        <span className="text-sm text-gray-700">{t(`collections.${activity.activity_type}`)}</span>
                        {channel && <span className="text-xs uppercase text-gray-400">{channel}</span>}
                        {outcome && (
                          <span
                            data-testid={`collection-action-status-${activity.activity_id}`}
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              outcome === 'failed'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {t(`collections.activityStatus_${outcome}`)}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-2 text-xs text-gray-400">
                        <time dateTime={activity.timestamp}>{new Date(activity.timestamp).toLocaleString()}</time>
                        {activity.created_by && <span>· {activity.created_by}</span>}
                        {amount != null && <span>· {formatNIS(amount)}</span>}
                      </div>
                      {activity.comment && (
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600">
                          {activity.comment}
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          {activities.length > 0 && error && (
            <p role="alert" className="px-5 py-3 text-sm text-red-600">{t('collections.actionsLoadMoreError')}</p>
          )}
          {nextCursor && (
            <div className="border-t border-gray-100 p-3">
              <button
                type="button"
                data-testid="collection-actions-load-more"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="w-full rounded-lg px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingMore ? t('collections.loadingActivities') : t('common.loadMore', { defaultValue: 'Load more' })}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ── Debt by Team Member Chart ───────────────────────────────────────── */

function DebtByTeamMemberChart({
  users,
  selectedUserId,
  onSelectUser,
  t,
}: {
  users: UserDebtSummary[];
  selectedUserId: string | null;
  onSelectUser: (userId: string | null) => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  if (!users.length) return null;

  const selectorSeries = users.map((u, index) => ({
    user_id: u.user_id,
    label: memberLabel(u.user_id, u.name, u.email, t),
    color: COLLECTION_TREND_COLORS[index % COLLECTION_TREND_COLORS.length],
  }));
  const visibleUsers = selectedUserId ? users.filter((u) => u.user_id === selectedUserId) : users;
  const data = visibleUsers.map((u) => ({
    user_id: u.user_id,
    name: memberLabel(u.user_id, u.name, u.email, t),
    [t('collections.chartTotalDebt')]: Math.round(u.total_open_debt),
    [t('collections.chartAgedDebt')]: Math.round(u.aged_debt),
  }));

  const fmt = (v: number) => `₪${(v / 1000).toFixed(0)}k`;
  const selectBarMember = (entry: unknown) => {
    const userId = (entry as { payload?: { user_id?: string } }).payload?.user_id;
    if (userId) onSelectUser(userId);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <p className="text-sm font-semibold text-gray-700 mb-0.5">{t('collections.chartTeamMembersTitle')}</p>
      <p className="text-xs text-gray-400 mb-4">{t('collections.chartTeamMembersSubtitle')}</p>
      <MemberChartSelector
        series={selectorSeries}
        selectedUserId={selectedUserId}
        onSelect={onSelectUser}
        t={t}
      />
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
          <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(value: any, name: any) => [
              `₪${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
              name,
            ]}
            contentStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar
            dataKey={t('collections.chartTotalDebt')}
            fill="#3b82f6"
            radius={[0, 4, 4, 0]}
            onClick={selectBarMember}
            cursor="pointer"
          />
          <Bar
            dataKey={t('collections.chartAgedDebt')}
            fill="#ef4444"
            radius={[0, 4, 4, 0]}
            onClick={selectBarMember}
            cursor="pointer"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Debt Trend Chart ────────────────────────────────────────────────── */

type DebtTrendPoint = { month: string; total_debt: number; aged_debt: number };

function DebtTrendChart({
  trend,
  t,
}: {
  trend: DebtTrendPoint[];
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  if (!trend.length) return null;

  const data = trend.map((p) => ({
    month: p.month,
    [t('collections.chartTotalDebt')]: Math.round(p.total_debt),
    [t('collections.chartAgedDebt')]: Math.round(p.aged_debt),
  }));

  const fmt = (v: number) => `₪${(v / 1000).toFixed(0)}k`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <p className="text-sm font-semibold text-gray-700 mb-0.5">{t('collections.chartTrendTitle')}</p>
      <p className="text-xs text-gray-400 mb-4">{t('collections.chartTrendSubtitle')}</p>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <defs>
            <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradAged" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} width={44} />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(value: any, name: any) => [
              `₪${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
              name,
            ]}
            contentStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Area
            type="monotone"
            dataKey={t('collections.chartTotalDebt')}
            stroke="#3b82f6"
            strokeWidth={2}
            fill="url(#gradTotal)"
          />
          <Area
            type="monotone"
            dataKey={t('collections.chartAgedDebt')}
            stroke="#ef4444"
            strokeWidth={2}
            fill="url(#gradAged)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Collections by Team Member Trend Chart ─────────────────────────── */

const COLLECTION_TREND_COLORS = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#4f46e5',
  '#65a30d',
];

function CollectionTrendByUserChart({
  trend,
  selectedUserId,
  onSelectUser,
  t,
}: {
  trend: CollectionTrendByUserPoint[];
  selectedUserId: string | null;
  onSelectUser: (userId: string | null) => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  if (!trend.length) return null;

  const months = Array.from(new Set(trend.map((p) => p.month))).sort();
  const series = Array.from(
    trend.reduce((map, p) => {
      const existing = map.get(p.user_id) || {
        user_id: p.user_id,
        label: memberLabel(p.user_id, p.name, p.email, t),
        total: 0,
      };
      existing.total += p.collected_amount || 0;
      map.set(p.user_id, existing);
      return map;
    }, new Map<string, { user_id: string; label: string; total: number }>())
      .values(),
  ).sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));

  if (!months.length || !series.length || series.every((s) => s.total <= 0)) return null;
  const seriesWithColors = series.map((s, index) => ({
    ...s,
    color: COLLECTION_TREND_COLORS[index % COLLECTION_TREND_COLORS.length],
  }));
  const visibleSeries = selectedUserId
    ? seriesWithColors.filter((s) => s.user_id === selectedUserId)
    : seriesWithColors;

  type ChartRow = { month: string } & Record<string, string | number>;
  const valuesByMonth = new Map<string, ChartRow>();
  months.forEach((month) => valuesByMonth.set(month, { month }));
  trend.forEach((p) => {
    const row = valuesByMonth.get(p.month);
    if (row) row[p.user_id] = Math.round(p.collected_amount || 0);
  });
  const data = months.map((month) => {
    const row = valuesByMonth.get(month) || { month };
    visibleSeries.forEach((s) => {
      row[s.user_id] = row[s.user_id] || 0;
    });
    return row;
  });

  const fmt = (v: number) => `₪${(v / 1000).toFixed(0)}k`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
      <p className="text-sm font-semibold text-gray-700 mb-0.5">{t('collections.chartCollectionsByMemberTitle')}</p>
      <p className="text-xs text-gray-400 mb-4">{t('collections.chartCollectionsByMemberSubtitle')}</p>
      <MemberChartSelector
        series={seriesWithColors}
        selectedUserId={selectedUserId}
        onSelect={onSelectUser}
        t={t}
      />
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} width={44} />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(value: any, name: any) => [
              `₪${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
              name,
            ]}
            contentStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {visibleSeries.map((s) => (
            <Line
              key={s.user_id}
              type="monotone"
              dataKey={s.user_id}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
              onClick={() => onSelectUser(s.user_id)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Debt Trend by Team Member Chart ────────────────────────────────── */

function DebtTrendByUserChart({
  trend,
  selectedUserId,
  onSelectUser,
  t,
}: {
  trend: DebtTrendByUserPoint[];
  selectedUserId: string | null;
  onSelectUser: (userId: string | null) => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const [metric, setMetric] = useState<'total_debt' | 'aged_debt'>('total_debt');

  if (!trend.length) return null;

  const months = Array.from(new Set(trend.map((p) => p.month))).sort();
  const series = Array.from(
    trend.reduce((map, p) => {
      const existing = map.get(p.user_id) || {
        user_id: p.user_id,
        label: memberLabel(p.user_id, p.name, p.email, t),
        peak: 0,
      };
      existing.peak = Math.max(existing.peak, p[metric] || 0);
      map.set(p.user_id, existing);
      return map;
    }, new Map<string, { user_id: string; label: string; peak: number }>())
      .values(),
  ).sort((a, b) => b.peak - a.peak || a.label.localeCompare(b.label));

  if (!months.length || !series.length) return null;
  const seriesWithColors = series.map((s, index) => ({
    ...s,
    color: COLLECTION_TREND_COLORS[index % COLLECTION_TREND_COLORS.length],
  }));
  const visibleSeries = selectedUserId
    ? seriesWithColors.filter((s) => s.user_id === selectedUserId)
    : seriesWithColors;

  type ChartRow = { month: string } & Record<string, string | number>;
  const valuesByMonth = new Map<string, ChartRow>();
  months.forEach((month) => valuesByMonth.set(month, { month }));
  trend.forEach((p) => {
    const row = valuesByMonth.get(p.month);
    if (row) row[p.user_id] = Math.round(p[metric] || 0);
  });
  const data = months.map((month) => {
    const row = valuesByMonth.get(month) || { month };
    visibleSeries.forEach((s) => {
      if (!(s.user_id in row)) row[s.user_id] = 0;
    });
    return row;
  });

  const fmt = (v: number) => `₪${(v / 1000).toFixed(0)}k`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
      <div className="flex items-start justify-between mb-0.5">
        <div>
          <p className="text-sm font-semibold text-gray-700">{t('collections.chartDebtTrendByMemberTitle')}</p>
          <p className="text-xs text-gray-400 mb-4">{t('collections.chartDebtTrendByMemberSubtitle')}</p>
        </div>
        <div className="flex gap-1 text-xs">
          <button
            onClick={() => setMetric('total_debt')}
            className={`px-2 py-1 rounded-md border transition-colors ${
              metric === 'total_debt'
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {t('collections.chartDebtTrendToggleTotal')}
          </button>
          <button
            onClick={() => setMetric('aged_debt')}
            className={`px-2 py-1 rounded-md border transition-colors ${
              metric === 'aged_debt'
                ? 'bg-red-500 text-white border-red-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {t('collections.chartDebtTrendToggleAged')}
          </button>
        </div>
      </div>
      <MemberChartSelector
        series={seriesWithColors}
        selectedUserId={selectedUserId}
        onSelect={onSelectUser}
        t={t}
      />
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} width={44} />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(value: any, name: any) => [
              `₪${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
              name,
            ]}
            contentStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {visibleSeries.map((s) => (
            <Line
              key={s.user_id}
              type="monotone"
              dataKey={s.user_id}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
              onClick={() => onSelectUser(s.user_id)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────── */

export default function Collections() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const {
    customers: allCustomers,
    updateCustomer,
    setCustomerAllowMessage,
    ensureCustomersLoaded,
  } = useRivhitData();
  const [data, setData] = useState<CollectionDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);

  // Filters
  const [minDebt, setMinDebt] = useState(0);
  const [minScore, setMinScore] = useState(0);
  const [agedOnly, setAgedOnly] = useState(false);
  const [agedDebtGreaterThan, setAgedDebtGreaterThan] = useState<number | null>(null);
  const [purchaseTrend, setPurchaseTrend] = useState<CollectionPresetPurchaseTrend | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedFilter, setAssignedFilter] = useState<string>('all'); // 'all' | '__unassigned__' | user_id
  const [chartMemberFilter, setChartMemberFilter] = useState<string | null>(null);
  const [teamAnalyticsExpanded, setTeamAnalyticsExpanded] = useState(false);

  // Workspace-shared saved presets
  const [collectionPresets, setCollectionPresets] = useState<CollectionPreset[]>([]);
  const [presetsLoadError, setPresetsLoadError] = useState(false);
  const [activePresetId, setActivePresetId] = useState<string>('all');
  const [presetMenuId, setPresetMenuId] = useState<string | null>(null);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<CollectionPreset | null>(null);
  const [presetDraft, setPresetDraft] = useState<CollectionPresetDraft>(emptyPresetDraft);
  const [presetSaving, setPresetSaving] = useState(false);
  const [presetSaveError, setPresetSaveError] = useState<string | null>(null);
  const [presetDeleteTarget, setPresetDeleteTarget] = useState<CollectionPreset | null>(null);
  const [presetDeleting, setPresetDeleting] = useState(false);

  // Extra customers from Rivhit (no debt) that match the search query
  const [rivhitSearchExtras, setRivhitSearchExtras] = useState<CollectionCustomerSummary[]>([]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setRivhitSearchExtras([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await searchRivhitCustomers(searchQuery.trim(), 50);
        const rivhitCustomers: CollectionCustomerSummary[] = (res.data || []).map((r: {
          customer_id: number;
          first_name?: string;
          last_name?: string;
          customer_name?: string;
          phone?: string | null;
        }) => {
          const name = [r.first_name, r.last_name].filter(Boolean).join(' ').trim()
            || r.customer_name
            || `Customer ${r.customer_id}`;
          return {
            customer_id: r.customer_id,
            customer_name: name,
            phone: r.phone ?? null,
            open_balance: 0,
            aged_debt: 0,
            oldest_unpaid_days: 0,
            collection_score: 0,
            last_activity: null,
          };
        });
        setRivhitSearchExtras(rivhitCustomers);
      } catch {
        setRivhitSearchExtras([]);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sorting
  const [sortField, setSortField] = useState<'collection_score' | 'open_balance' | 'aged_debt' | 'oldest_unpaid_days'>('collection_score');
  const [sortDesc, setSortDesc] = useState(true);

  // Selection
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkCollectionActionResponse | null>(null);

  // Expanded activity rows
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [activityRefreshVersion, setActivityRefreshVersion] = useState(0);

  // WhatsApp modal
  const [whatsappTarget, setWhatsappTarget] = useState<CollectionCustomerSummary | null>(null);

  // Manual contact modal
  const [manualContactTarget, setManualContactTarget] = useState<CollectionCustomerSummary | null>(null);
  const [manualContactComment, setManualContactComment] = useState('');
  const [manualContactSubmitting, setManualContactSubmitting] = useState(false);
  const [manualContactError, setManualContactError] = useState<string | null>(null);
  const [manualContactSuccess, setManualContactSuccess] = useState<string | null>(null);

  // Customer details modal
  const [detailsCustomer, setDetailsCustomer] = useState<CollectionCustomerSummary | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<RivhitCustomer | null>(null);

  // Load full Rivhit customer objects lazily — only when the detail/edit modal opens.
  // The allow_message flag is now included in the collections dashboard response itself,
  // so there is no need to prefetch all 58K customers on page mount.

  const [loadingSlow, setLoadingSlow] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    setLoadingSlow(false);
    setError(null);

    // Show a "taking longer" notice after 10 seconds
    const slowTimer = setTimeout(() => setLoadingSlow(true), 10_000);

    getCollectionsDashboard()
      .then(setData)
      .catch((e) => {
        const status = e?.response?.status;
        const isTimeout = e?.code === 'ECONNABORTED' || (!e?.response && /timeout/i.test(e?.message || ''));
        if (status === 504 || isTimeout) {
          setError(t('collections.timeoutError', 'The request timed out — the collections data is large. Please try again in a moment.'));
        } else if (status === 429 || status === 503) {
          setError(t('collections.serviceUnavailable', 'The accounting service is temporarily unavailable. Please try again shortly.'));
        } else if (status && status >= 500) {
          setError(t('collections.serverError', 'The accounting service encountered an error. Please try again in a moment.'));
        } else {
          setError(e?.message || 'Failed to load');
        }
      })
      .finally(() => {
        clearTimeout(slowTimer);
        setLoading(false);
        setLoadingSlow(false);
      });
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;
    getCollectionPresets()
      .then((items) => {
        if (!cancelled) {
          setCollectionPresets([...items].sort((a, b) => a.name.localeCompare(b.name)));
          setPresetsLoadError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setPresetsLoadError(true);
      });
    return () => { cancelled = true; };
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!data) return [];
    let list = data.customers;
    if (minDebt > 0) list = list.filter((c) => c.open_balance >= minDebt);
    if (minScore > 0) list = list.filter((c) => c.collection_score >= minScore);
    if (agedOnly) list = list.filter((c) => c.aged_debt > 0);
    if (agedDebtGreaterThan != null) list = list.filter((c) => c.aged_debt > agedDebtGreaterThan);
    if (purchaseTrend) list = list.filter((c) => c.tracked_item_growth_status === purchaseTrend);
    if (assignedFilter !== 'all') {
      if (assignedFilter === '__unassigned__') {
        list = list.filter((c) => !c.assigned_members || c.assigned_members.length === 0);
      } else {
        list = list.filter((c) => (c.assigned_members || []).some((m) => m.user_id === assignedFilter));
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((c) => c.customer_name.toLowerCase().includes(q));
      // Also include zero-debt customers from Rivhit not already in the debt list
      const existingIds = new Set(list.map((c) => c.customer_id));
      const extras = rivhitSearchExtras.filter((c) => !existingIds.has(c.customer_id));
      list = [...list, ...extras];
    }
    const dir = sortDesc ? -1 : 1;
    return [...list].sort((a, b) => ((a[sortField] ?? 0) - (b[sortField] ?? 0)) * dir);
  }, [data, minDebt, minScore, agedOnly, agedDebtGreaterThan, purchaseTrend, assignedFilter, searchQuery, sortField, sortDesc, rivhitSearchExtras]);

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDesc(!sortDesc);
    else { setSortField(field); setSortDesc(true); }
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredCustomers.length) setSelected(new Set());
    else setSelected(new Set(filteredCustomers.map((c) => c.customer_id)));
  };

  const handleBulkSms = async () => {
    if (!selected.size) return;
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const result = await bulkCollectionAction({
        customer_ids: Array.from(selected),
        action_type: 'send_whatsapp',
        include_payment_link: true,
      });
      setBulkResult(result);
      setSelected(new Set());
      setActivityRefreshVersion((version) => version + 1);
      fetchData();
    } catch {
      // handled silently
    } finally {
      setBulkLoading(false);
    }
  };

  useEffect(() => {
    if (!manualContactSuccess) return;
    const timer = window.setTimeout(() => setManualContactSuccess(null), 5000);
    return () => window.clearTimeout(timer);
  }, [manualContactSuccess]);

  const openManualContactModal = (customer: CollectionCustomerSummary) => {
    setManualContactTarget(customer);
    setManualContactComment('');
    setManualContactError(null);
  };

  const closeManualContactModal = () => {
    if (manualContactSubmitting) return;
    setManualContactTarget(null);
    setManualContactComment('');
    setManualContactError(null);
  };

  const handleMarkContacted = async () => {
    if (!manualContactTarget) return;
    setManualContactSubmitting(true);
    setManualContactError(null);
    try {
      await logCollectionActivity({
        customer_id: manualContactTarget.customer_id,
        activity_type: 'manual_contact',
        details: { source: 'collections_page' },
        comment: manualContactComment.trim() || undefined,
      });
      const customerName = manualContactTarget.customer_name;
      setManualContactTarget(null);
      setManualContactComment('');
      setActivityRefreshVersion((version) => version + 1);
      setManualContactSuccess(t('collections.manualContactSuccess', { customer: customerName }));
      fetchData();
    } catch {
      setManualContactError(
        t('collections.manualContactError', 'Could not save the contact. Please try again.'),
      );
    } finally {
      setManualContactSubmitting(false);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sortArrow = (field: typeof sortField) =>
    sortField === field ? (sortDesc ? ' ▼' : ' ▲') : '';

  // Options for the "filter by team member" dropdown — derived from the
  // debt_by_user aggregation so the labels match the team summary table.
  const memberOptions = useMemo(() => {
    if (!data) return [] as { user_id: string; label: string }[];
    return data.debt_by_user.map((u) => ({
      user_id: u.user_id,
      label: memberLabel(u.user_id, u.name, u.email, t),
    }));
  }, [data, t]);

  const chartMemberIds = useMemo(() => {
    if (!data) return new Set<string>();
    return new Set([
      ...data.debt_by_user.map((u) => u.user_id),
      ...(data.collection_trend_by_user || []).map((p) => p.user_id),
      ...(data.debt_trend_by_user || []).map((p) => p.user_id),
    ]);
  }, [data]);

  const hasTeamMemberAnalytics = Boolean(
    data && (
      data.debt_by_user.length > 0
      || (data.collection_trend_by_user?.length ?? 0) > 0
      || (data.debt_trend_by_user?.length ?? 0) > 0
    ),
  );

  useEffect(() => {
    if (chartMemberFilter && !chartMemberIds.has(chartMemberFilter)) {
      setChartMemberFilter(null);
    }
  }, [chartMemberFilter, chartMemberIds]);

  const resetPresetCriteria = () => {
    setMinDebt(0);
    setMinScore(0);
    setAgedOnly(false);
    setAgedDebtGreaterThan(null);
    setPurchaseTrend(null);
    setAssignedFilter('all');
    setActivePresetId('all');
  };

  const applyPreset = (presetId: string, filters: CollectionPresetFilters, force = false) => {
    if (!force && activePresetId === presetId) {
      resetPresetCriteria();
      return;
    }
    setMinDebt(filters.min_open_balance ?? 0);
    setMinScore(filters.min_collection_score ?? 0);
    setAgedOnly(false);
    setAgedDebtGreaterThan(filters.aged_debt_greater_than ?? null);
    setPurchaseTrend(filters.purchase_trend ?? null);
    setAssignedFilter(
      filters.unassigned_only ? '__unassigned__' : (filters.assigned_member_id || 'all'),
    );
    setActivePresetId(presetId);
    setPresetMenuId(null);
  };

  const openCreatePresetModal = () => {
    setEditingPreset(null);
    setPresetDraft({
      name: '',
      agedDebtGreaterThan: agedDebtGreaterThan != null
        ? String(agedDebtGreaterThan)
        : (agedOnly ? '0' : ''),
      minOpenBalance: minDebt > 0 ? String(minDebt) : '',
      minCollectionScore: minScore > 0 ? String(minScore) : '',
      purchaseTrend: purchaseTrend || '',
      assignment: assignedFilter,
    });
    setPresetSaveError(null);
    setPresetModalOpen(true);
  };

  const openEditPresetModal = (preset: CollectionPreset) => {
    setEditingPreset(preset);
    setPresetDraft(draftFromPreset(preset));
    setPresetSaveError(null);
    setPresetMenuId(null);
    setPresetModalOpen(true);
  };

  const savePreset = async () => {
    const name = presetDraft.name.trim().replace(/\s+/g, ' ');
    const filters = presetFiltersFromDraft(presetDraft);
    const hasCondition = Object.entries(filters).some(([key, value]) => (
      key === 'unassigned_only' ? value === true : value != null
    ));
    if (!name) {
      setPresetSaveError(t('collections.presetNameRequired'));
      return;
    }
    if (!hasCondition) {
      setPresetSaveError(t('collections.presetConditionRequired'));
      return;
    }
    setPresetSaving(true);
    setPresetSaveError(null);
    try {
      const saved = editingPreset
        ? await updateCollectionPreset(editingPreset.id, { name, filters })
        : await createCollectionPreset({ name, filters });
      setCollectionPresets((current) => (
        [...current.filter((preset) => preset.id !== saved.id), saved]
          .sort((a, b) => a.name.localeCompare(b.name))
      ));
      setPresetModalOpen(false);
      setEditingPreset(null);
      applyPreset(saved.id, saved.filters, true);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      setPresetSaveError(
        error?.response?.status === 409
          ? t('collections.presetDuplicateName')
          : (typeof detail === 'string' ? detail : t('collections.presetSaveError')),
      );
    } finally {
      setPresetSaving(false);
    }
  };

  const deletePreset = async () => {
    if (!presetDeleteTarget) return;
    setPresetDeleting(true);
    try {
      await deleteCollectionPreset(presetDeleteTarget.id);
      setCollectionPresets((current) => current.filter((preset) => preset.id !== presetDeleteTarget.id));
      if (activePresetId === presetDeleteTarget.id) resetPresetCriteria();
      setPresetDeleteTarget(null);
    } catch {
      setPresetSaveError(t('collections.presetDeleteError'));
      setPresetDeleteTarget(null);
    } finally {
      setPresetDeleting(false);
    }
  };

  const canManagePreset = (preset: CollectionPreset) => (
    user?.role === 'owner' || user?.role === 'admin' || preset.created_by === user?.id
  );

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await recalculateCollectionKpis();
      fetchData();
    } catch {
      // handled silently
    } finally {
      setRecalculating(false);
    }
  };

  const collectionLabel = (key: string, fallback: string, values?: Record<string, unknown>) => {
    const resolved = t(key, { defaultValue: fallback, ...values });
    return resolved === key ? fallback : resolved;
  };

  const handlePrint = () => {
    const dir = i18n.dir();
    const isRtl = dir === 'rtl';
    const locale = i18n.language?.startsWith('he') ? 'he-IL' : 'en-US';
    const now = new Date().toLocaleString(locale);
    const priorityLabel = (score: number) => {
      if (score >= 70) return collectionLabel('collections.priorityTagImmediate', 'Collect now');
      if (score >= 40) return collectionLabel('collections.priorityTagFollowUp', 'Follow up');
      return collectionLabel('collections.priorityTagMonitor', 'Monitor');
    };
    const priorityColor = (score: number) => {
      if (score >= 70) return '#fee2e2;color:#991b1b';
      if (score >= 40) return '#fef9c3;color:#854d0e';
      return '#dcfce7;color:#166534';
    };
    const rows = filteredCustomers.map((c) => `
      <tr>
        <td>${escapeHtml(c.customer_name)}</td>
        <td>${escapeHtml(c.phone || '—')}</td>
        <td style="text-align:end">${formatNIS(c.open_balance)}</td>
        <td style="text-align:end;color:${c.aged_debt > 0 ? '#dc2626' : '#9ca3af'}">${c.aged_debt > 0 ? formatNIS(c.aged_debt) : '—'}</td>
        <td style="text-align:end">${c.oldest_unpaid_days > 0 ? `${c.oldest_unpaid_days} ${escapeHtml(collectionLabel('collections.days', 'days'))}` : '—'}</td>
        <td>${c.last_payment_date ? new Date(c.last_payment_date).toLocaleDateString(locale) : '—'}</td>
        <td><span class="priority" style="background:${priorityColor(c.collection_score)}">${escapeHtml(priorityLabel(c.collection_score))}</span></td>
        <td>${escapeHtml((c.assigned_members || []).map((m) => m.name || m.email || m.user_id).join(', ') || collectionLabel('collections.unassigned', 'Unassigned'))}</td>
      </tr>`).join('');
    const printTitle = collectionLabel('collections.printTitle', 'Debt Collection Report');
    const printGeneratedAt = collectionLabel('collections.printGeneratedAt', 'Generated: {{date}}', { date: now });
    const printCustomers = collectionLabel('collections.printCustomers', '{{count}} customers', { count: filteredCustomers.length });

    const html = `<!DOCTYPE html>
<html dir="${dir}" lang="${escapeHtml(i18n.language || (isRtl ? 'he' : 'en'))}">
<head>
  <meta charset="utf-8"/>
  <title>${escapeHtml(printTitle)}</title>
  <style>
    @page { size: A4 landscape; margin: 12mm; }
    * { box-sizing: border-box; }
    html, body { width: 100%; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 10px; color: #111; direction: ${dir}; }
    .print-page { width: 100%; max-width: 100%; }
    h1 { font-size: 18px; margin: 0 0 4px; }
    .meta { font-size: 11px; color: #6b7280; margin-bottom: 16px; }
    table { width: 100%; max-width: 100%; border-collapse: collapse; table-layout: fixed; }
    th { background: #f3f4f6; text-align: start; padding: 6px 6px; font-size: 9px; text-transform: uppercase; border-bottom: 2px solid #e5e7eb; }
    td { padding: 5px 6px; border-bottom: 1px solid #f3f4f6; vertical-align: top; overflow-wrap: anywhere; }
    tr:last-child td { border-bottom: none; }
    .priority { display: inline-block; padding: 2px 6px; border-radius: 9999px; font-size: 9px; line-height: 1.2; white-space: normal; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <main class="print-page">
    <h1>${escapeHtml(printTitle)}</h1>
    <p class="meta">
      ${escapeHtml(printGeneratedAt)} &nbsp;·&nbsp;
      ${escapeHtml(printCustomers)}
    </p>
    <table>
      <colgroup>
        <col style="width:22%"/>
        <col style="width:12%"/>
        <col style="width:12%"/>
        <col style="width:12%"/>
        <col style="width:11%"/>
        <col style="width:11%"/>
        <col style="width:10%"/>
        <col style="width:10%"/>
      </colgroup>
      <thead>
        <tr>
          <th>${escapeHtml(collectionLabel('collections.customer', 'Customer'))}</th>
          <th>${escapeHtml(collectionLabel('collections.printPhone', 'Phone'))}</th>
          <th style="text-align:end">${escapeHtml(collectionLabel('collections.openBalance', 'Open Balance'))}</th>
          <th style="text-align:end">${escapeHtml(collectionLabel('collections.agedDebtAmount', 'Aged Debt'))}</th>
          <th style="text-align:end">${escapeHtml(collectionLabel('collections.oldestUnpaid', 'Oldest Unpaid'))}</th>
          <th>${escapeHtml(collectionLabel('collections.lastPaymentDate', 'Last Payment'))}</th>
          <th>${escapeHtml(collectionLabel('collections.score', 'Priority'))}</th>
          <th>${escapeHtml(collectionLabel('collections.assignedTo', 'Assigned To'))}</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </main>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 250);
  };

  /* ── Render ───────────────────────────────────────────────────────── */

  if (loading && !data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        {loadingSlow && (
          <p className="text-sm text-amber-600 text-center max-w-md">
            {t('collections.loadingSlow', 'Loading is taking longer than usual — the collections data is large. Please wait...')}
          </p>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button onClick={fetchData} className="text-blue-500 hover:underline">
          Retry
        </button>
      </div>
    );
  }

  const kpis = data?.kpis;
  const printButtonLabel = collectionLabel('collections.printButton', 'Print');

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {manualContactSuccess && (
        <div
          role="status"
          data-testid="manual-contact-success"
          className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
        >
          {manualContactSuccess}
        </div>
      )}

      {/* Header */}
      <div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('collections.title')}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('collections.subtitle')}</p>
          </div>
          <button
            onClick={handleRecalculate}
            disabled={recalculating}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {recalculating ? t('collections.calculatingNow') : t('collections.calculateNow')}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {kpis && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {kpis.last_calculated_at ? (
              <span>{t('collections.lastCalculatedAt', { date: new Date(kpis.last_calculated_at).toLocaleString() })}</span>
            ) : (
              <span>{t('collections.metricsUnavailableHint')}</span>
            )}
            {kpis.cache_status === 'not_available' && (
              <span className="text-orange-600">{t('collections.runCalculationHint')}</span>
            )}
          </div>
          {(kpis.uncertain_open_invoices_count || 0) > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert" data-testid="collections-balance-warning">
              {t(
                'documents.uncertainBalanceWarning',
                '{{count}} invoice balance could not be verified and is excluded from open-debt totals.',
                { count: kpis.uncertain_open_invoices_count },
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KPICard label={t('collections.totalOpenDebt')} value={formatNIS(kpis.total_open_debt)} description={t('collections.tooltip_totalOpenDebt')} />
            <KPICard label={t('collections.overdueAmount')} value={formatNIS(kpis.overdue_amount)} description={t('collections.tooltip_overdueAmount')} />
            <KPICard label={t('collections.dueThisWeek')} value={formatNIS(kpis.due_this_week)} description={t('collections.tooltip_dueThisWeek')} />
            <KPICard
              label={t('collections.paidThisMonth')}
              value={kpis.paid_this_month == null ? t('collections.notAvailable') : formatNIS(kpis.paid_this_month)}
              sub={kpis.paid_this_month == null ? t('collections.runCalculationHint') : undefined}
              unavailable={kpis.paid_this_month == null}
              description={t('collections.tooltip_paidThisMonth')}
            />
            <KPICard
              label={t('collections.customersWithRepeatedDelays')}
              value={kpis.customers_with_repeated_delays == null ? t('collections.notAvailable') : String(kpis.customers_with_repeated_delays)}
              sub={kpis.customers_with_repeated_delays == null ? t('collections.runCalculationHint') : undefined}
              unavailable={kpis.customers_with_repeated_delays == null}
              description={t('collections.tooltip_customersWithRepeatedDelays')}
            />
            <KPICard
              label={t('collections.failedOrIgnoredWhatsAppMessages')}
              value={kpis.failed_or_ignored_whatsapp_messages == null ? t('collections.notAvailable') : String(kpis.failed_or_ignored_whatsapp_messages)}
              sub={kpis.failed_or_ignored_whatsapp_messages == null ? t('collections.runCalculationHint') : undefined}
              unavailable={kpis.failed_or_ignored_whatsapp_messages == null}
              description={t('collections.tooltip_failedOrIgnoredWhatsAppMessages')}
            />
          </div>
        </div>
      )}

      {/* General debt trend remains visible independently of member analytics. */}
      {kpis?.debt_trend && kpis.debt_trend.length > 0 && (
        <DebtTrendChart trend={kpis.debt_trend} t={t} />
      )}

      {/* Team-member analytics */}
      {data && hasTeamMemberAnalytics && (
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <button
            type="button"
            data-testid="team-member-analytics-toggle"
            aria-expanded={teamAnalyticsExpanded}
            aria-controls="team-member-analytics-content"
            onClick={() => setTeamAnalyticsExpanded((expanded) => !expanded)}
            className="w-full flex items-center justify-between gap-3 px-5 py-4 text-start text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span>{t('collections.teamMemberAnalytics')}</span>
            <svg
              aria-hidden="true"
              className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${teamAnalyticsExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {teamAnalyticsExpanded && (
            <div
              id="team-member-analytics-content"
              data-testid="team-member-analytics-content"
              className="border-t border-gray-100 bg-gray-50 p-4 space-y-4"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {data.debt_by_user.length > 0 && (
                  <DebtByTeamMemberChart
                    users={data.debt_by_user}
                    selectedUserId={chartMemberFilter}
                    onSelectUser={setChartMemberFilter}
                    t={t}
                  />
                )}
                {data.collection_trend_by_user && data.collection_trend_by_user.length > 0 && (
                  <CollectionTrendByUserChart
                    trend={data.collection_trend_by_user}
                    selectedUserId={chartMemberFilter}
                    onSelectUser={setChartMemberFilter}
                    t={t}
                  />
                )}
                {data.debt_trend_by_user && data.debt_trend_by_user.length > 0 && (
                  <DebtTrendByUserChart
                    trend={data.debt_trend_by_user}
                    selectedUserId={chartMemberFilter}
                    onSelectUser={setChartMemberFilter}
                    t={t}
                  />
                )}
              </div>

              {data.debt_by_user.length > 0 && (
                <DebtByUserTable users={data.debt_by_user} t={t} />
              )}
            </div>
          )}
        </section>
      )}

      <CollectionActionsFeed refreshVersion={activityRefreshVersion} t={t} />

      {/* Customer table presets */}
      <section className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm" data-testid="collection-presets">
        <div className="flex flex-wrap items-center gap-2">
          <span className="me-1 text-sm font-medium text-gray-600">{t('collections.presets')}:</span>
          <button
            type="button"
            onClick={resetPresetCriteria}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${activePresetId === 'all' ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            {t('collections.presetAllCustomers')}
          </button>
          <button
            type="button"
            title={t('collections.presetOldDebtTooltip')}
            onClick={() => applyPreset('builtin-old-debt', { aged_debt_greater_than: 100 })}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${activePresetId === 'builtin-old-debt' ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            {t('collections.presetOldDebt')}
          </button>
          {collectionPresets.map((preset) => (
            <div key={preset.id} className="relative inline-flex">
              <button
                type="button"
                onClick={() => applyPreset(preset.id, preset.filters)}
                className={`rounded-s-full border border-e-0 px-3 py-1.5 text-sm font-medium transition-colors ${activePresetId === preset.id ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {preset.name}
              </button>
              {canManagePreset(preset) && (
                <button
                  type="button"
                  aria-label={t('collections.presetManage', { name: preset.name })}
                  aria-expanded={presetMenuId === preset.id}
                  onClick={() => setPresetMenuId((current) => current === preset.id ? null : preset.id)}
                  className={`rounded-e-full border px-2 py-1.5 text-sm ${activePresetId === preset.id ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  ⋯
                </button>
              )}
              {presetMenuId === preset.id && canManagePreset(preset) && (
                <div className="absolute end-0 top-full z-20 mt-1 min-w-32 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <button type="button" onClick={() => openEditPresetModal(preset)} className="block w-full px-3 py-2 text-start text-sm text-gray-700 hover:bg-gray-50">
                    {t('common.edit', 'Edit')}
                  </button>
                  <button type="button" onClick={() => { setPresetDeleteTarget(preset); setPresetMenuId(null); }} className="block w-full px-3 py-2 text-start text-sm text-red-600 hover:bg-red-50">
                    {t('common.delete', 'Delete')}
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={openCreatePresetModal}
            className="rounded-full border border-dashed border-blue-400 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
          >
            + {t('collections.newPreset')}
          </button>
        </div>
        {presetsLoadError && (
          <p role="status" className="mt-2 text-sm text-amber-700">{t('collections.presetsLoadError')}</p>
        )}
        {presetSaveError && !presetModalOpen && (
          <p role="alert" className="mt-2 text-sm text-red-600">{presetSaveError}</p>
        )}
      </section>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-gray-600">{t('collections.filters')}:</span>
          <input
            type="text"
            placeholder={t('collections.customer')}
            className="border rounded-lg px-3 py-1.5 text-sm w-48"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <label className="flex items-center gap-1 text-sm">
            {t('collections.minDebt')}:
            <input
              type="number"
              className="border rounded-lg px-2 py-1.5 text-sm w-24"
              value={minDebt || ''}
              onChange={(e) => { setMinDebt(Number(e.target.value) || 0); setActivePresetId(''); }}
            />
          </label>
          <label className="flex items-center gap-1 text-sm">
            {t('collections.minScore')}:
            <input
              type="number"
              className="border rounded-lg px-2 py-1.5 text-sm w-20"
              value={minScore || ''}
              onChange={(e) => { setMinScore(Number(e.target.value) || 0); setActivePresetId(''); }}
              min={0}
              max={100}
            />
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={agedOnly}
              onChange={(e) => { setAgedOnly(e.target.checked); setActivePresetId(''); }}
              className="rounded"
            />
            {t('collections.showAgedOnly')}
          </label>
          <label className="flex items-center gap-1 text-sm">
            {t('collections.filterByMember')}:
            <select
              className="border rounded-lg px-2 py-1.5 text-sm"
              value={assignedFilter}
              onChange={(e) => { setAssignedFilter(e.target.value); setActivePresetId(''); }}
            >
              <option value="all">{t('collections.allMembers')}</option>
              {memberOptions.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.label}</option>
              ))}
            </select>
          </label>
          {(minDebt > 0 || minScore > 0 || agedOnly || agedDebtGreaterThan != null || purchaseTrend || searchQuery || assignedFilter !== 'all') && (
            <button
              className="text-sm text-blue-500 hover:underline"
              onClick={() => { resetPresetCriteria(); setSearchQuery(''); }}
            >
              {t('collections.clearFilters')}
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selected.size > 0 && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-3 flex items-center gap-4">
          <span className="text-sm font-medium text-blue-800">
            {selected.size} {t('collections.selected')}
          </span>
          <button
            disabled={bulkLoading}
            onClick={handleBulkSms}
            className="bg-blue-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50"
          >
            💬 {t('collections.bulkSms', 'Send SMS to Selected')}
          </button>
          {bulkLoading && <span className="text-sm text-gray-500">{t('collections.processing')}</span>}
        </div>
      )}

      {/* Bulk Result */}
      {bulkResult && (
        <div className="bg-green-50 rounded-xl border border-green-200 p-3 text-sm">
          {t('collections.bulkSuccess', { succeeded: bulkResult.succeeded, total: bulkResult.total })}
          {bulkResult.failed > 0 && (
            <span className="text-red-600 ltr:ml-2 rtl:mr-2">
              {t('collections.bulkFailed', { failed: bulkResult.failed })}
            </span>
          )}
          <button onClick={() => setBulkResult(null)} className="ltr:ml-3 rtl:mr-3 text-gray-400 hover:text-gray-600">✕</button>
        </div>
      )}

      {/* Customer Table */}
      {filteredCustomers.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          {activePresetId && activePresetId !== 'all'
            ? t('collections.noPresetMatches')
            : t('collections.noDebt')}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between">
            <span className="text-xs text-gray-500">{t('collections.printCustomers', { count: filteredCustomers.length })}</span>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {printButtonLabel}
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-gray-600">
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={selected.size === filteredCustomers.length && filteredCustomers.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="p-3 text-start">
                  <div className="flex items-center gap-1">
                    {t('collections.customer')}
                    <InfoTooltip text={t('collections.tooltip_columnCustomer')} />
                  </div>
                </th>
                <th className="p-3 text-start cursor-pointer select-none" onClick={() => toggleSort('open_balance')}>
                  <div className="flex items-center gap-1">
                    {t('collections.openBalance')}{sortArrow('open_balance')}
                    <InfoTooltip text={t('collections.tooltip_columnOpenBalance')} />
                  </div>
                </th>
                <th className="p-3 text-start cursor-pointer select-none" onClick={() => toggleSort('aged_debt')}>
                  <div className="flex items-center gap-1">
                    {t('collections.agedDebtAmount')}{sortArrow('aged_debt')}
                    <InfoTooltip text={t('collections.tooltip_columnAgedDebt')} />
                  </div>
                </th>
                <th className="p-3 text-start cursor-pointer select-none" onClick={() => toggleSort('oldest_unpaid_days')}>
                  <div className="flex items-center gap-1">
                    {t('collections.oldestUnpaid')}{sortArrow('oldest_unpaid_days')}
                    <InfoTooltip text={t('collections.tooltip_columnOldestUnpaid')} />
                  </div>
                </th>
                <th className="p-3 text-start">
                  <div className="flex items-center gap-1">
                    {t('collections.lastPaymentDate')}
                    <InfoTooltip text={t('collections.tooltip_columnLastPaymentDate')} />
                  </div>
                </th>
                <th className="p-3 text-start cursor-pointer select-none" onClick={() => toggleSort('collection_score')}>
                  <div className="flex items-center gap-1">
                    {t('collections.score')}{sortArrow('collection_score')}
                    <InfoTooltip text={t('collections.tooltip_columnScore')} />
                  </div>
                </th>
                <th className="p-3 text-start">
                  <div className="flex items-center gap-1">
                    {t('collections.lastActivity')}
                    <InfoTooltip text={t('collections.tooltip_columnLastActivity')} />
                  </div>
                </th>
                <th className="p-3 text-start">
                  <div className="flex items-center gap-1">
                    {t('collections.assignedTo')}
                    <InfoTooltip text={t('collections.tooltip_columnAssignedTo')} />
                  </div>
                </th>
                <th className="p-3 text-start">
                  <div className="flex items-center gap-1">
                    {t('collections.messagesApproved')}
                    <InfoTooltip text={t('collections.tooltip_columnMessagesApproved')} />
                  </div>
                </th>
                <th className="p-3 text-start">{t('collections.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((c) => (
                <>
                  <tr key={c.customer_id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.has(c.customer_id)}
                        onChange={() => toggleSelect(c.customer_id)}
                        className="rounded"
                      />
                    </td>
                    <td className="p-3">
                      <button
                        className="text-start hover:underline focus:outline-none"
                        onClick={() => { setDetailsCustomer(c); ensureCustomersLoaded(); }}
                      >
                        <div className="font-medium text-blue-700 hover:text-blue-900">{c.customer_name}</div>
                        {c.phone && <div className="text-xs text-gray-400">{c.phone}</div>}
                      </button>
                    </td>
                    <td className="p-3 font-medium">{formatNIS(c.open_balance)}</td>
                    <td className="p-3">
                      {c.aged_debt > 0 ? (
                        <span className="text-red-600 font-medium">{formatNIS(c.aged_debt)}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {c.oldest_unpaid_days > 0 ? (
                        <span>{c.oldest_unpaid_days} {t('collections.days')}</span>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-sm">
                      {c.last_payment_date ? (
                        <span>{new Date(c.last_payment_date).toLocaleDateString()}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span
                        title={t('collections.priorityScoreValue', { score: c.collection_score })}
                        className={`inline-block min-w-20 text-center px-2 py-0.5 rounded-full text-xs font-medium ${scoreColor(c.collection_score)}`}
                      >
                        {t(priorityTagKey(c.collection_score))}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-gray-500">
                      {c.last_activity ? (
                        <span>
                          {ACTIVITY_ICONS[c.last_activity.activity_type] || '📋'}{' '}
                          {new Date(c.last_activity.timestamp).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-orange-500">{t('collections.neverContacted')}</span>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {c.assigned_members && c.assigned_members.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {c.assigned_members.map((m) => (
                            <span
                              key={m.user_id}
                              title={m.email || m.user_id}
                              className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100"
                            >
                              {m.name || m.email || m.user_id}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-orange-500">{t('collections.unassigned')}</span>
                      )}
                    </td>
                    <td className="p-3 text-sm">
                      {c.allow_message ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                          {t('collections.messagesApprovedYes')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">
                          {t('collections.messagesApprovedNo')}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <button
                          title={t('collections.sendSms', 'Send SMS')}
                          onClick={() => setWhatsappTarget(c)}
                          className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                        >
                          💬
                        </button>
                        <button
                          title={t('collections.markContacted')}
                          data-testid={`manual-contact-${c.customer_id}`}
                          onClick={() => openManualContactModal(c)}
                          className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                        >
                          🤝
                        </button>
                        <button
                          title={t('collections.viewActivity')}
                          onClick={() => toggleExpand(c.customer_id)}
                          className={`p-1.5 rounded hover:bg-gray-100 ${expandedRows.has(c.customer_id) ? 'bg-gray-100' : ''}`}
                        >
                          📋
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedRows.has(c.customer_id) && (
                    <tr key={`${c.customer_id}-activity`}>
                      <td colSpan={11} className="bg-gray-50 px-6 py-3 border-b">
                        <div className="text-xs font-medium text-gray-500 mb-2">
                          {t('collections.activityLog')}
                        </div>
                        <ActivityTimeline
                          customerId={c.customer_id}
                          refreshVersion={activityRefreshVersion}
                          t={t}
                        />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/edit collection preset modal */}
      {presetModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => { if (!presetSaving) setPresetModalOpen(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="collection-preset-title"
            data-testid="collection-preset-dialog"
            className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="collection-preset-title" className="text-lg font-semibold text-gray-900">
              {editingPreset ? t('collections.editPreset') : t('collections.createPreset')}
            </h2>
            <p className="mt-1 text-sm text-gray-500">{t('collections.presetModalHint')}</p>

            <label className="mt-5 block text-sm font-medium text-gray-700">
              {t('collections.presetName')}
              <input
                autoFocus
                value={presetDraft.name}
                maxLength={80}
                onChange={(event) => { setPresetDraft((draft) => ({ ...draft, name: event.target.value })); setPresetSaveError(null); }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-gray-700">
                {t('collections.presetOldDebtGreaterThan')}
                <input
                  type="number"
                  min={0}
                  value={presetDraft.agedDebtGreaterThan}
                  onChange={(event) => { setPresetDraft((draft) => ({ ...draft, agedDebtGreaterThan: event.target.value })); setPresetSaveError(null); }}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="₪"
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                {t('collections.presetMinTotalDebt')}
                <input
                  type="number"
                  min={0}
                  value={presetDraft.minOpenBalance}
                  onChange={(event) => { setPresetDraft((draft) => ({ ...draft, minOpenBalance: event.target.value })); setPresetSaveError(null); }}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="₪"
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                {t('collections.presetMinScore')}
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={presetDraft.minCollectionScore}
                  onChange={(event) => { setPresetDraft((draft) => ({ ...draft, minCollectionScore: event.target.value })); setPresetSaveError(null); }}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                {t('collections.presetPurchaseTrend')}
                <select
                  value={presetDraft.purchaseTrend}
                  onChange={(event) => { setPresetDraft((draft) => ({ ...draft, purchaseTrend: event.target.value as CollectionPresetDraft['purchaseTrend'] })); setPresetSaveError(null); }}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">{t('collections.presetTrendAny')}</option>
                  <option value="struggling">{t('collections.presetTrendDeclining')}</option>
                  <option value="stable">{t('collections.presetTrendStable')}</option>
                  <option value="growing">{t('collections.presetTrendGrowing')}</option>
                </select>
              </label>
            </div>

            <label className="mt-4 block text-sm font-medium text-gray-700">
              {t('collections.presetAssignment')}
              <select
                value={presetDraft.assignment}
                onChange={(event) => { setPresetDraft((draft) => ({ ...draft, assignment: event.target.value })); setPresetSaveError(null); }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="all">{t('collections.allMembers')}</option>
                {memberOptions.map((member) => (
                  <option key={member.user_id} value={member.user_id}>{member.label}</option>
                ))}
              </select>
            </label>

            {presetSaveError && <p role="alert" className="mt-4 text-sm text-red-600">{presetSaveError}</p>}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={presetSaving}
                onClick={() => setPresetModalOpen(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                data-testid="collection-preset-save"
                disabled={presetSaving}
                onClick={savePreset}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {presetSaving ? t('collections.presetSaving') : t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete collection preset confirmation */}
      {presetDeleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => { if (!presetDeleting) setPresetDeleteTarget(null); }}>
          <div role="alertdialog" aria-modal="true" className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900">{t('collections.deletePresetTitle')}</h2>
            <p className="mt-2 text-sm text-gray-600">{t('collections.deletePresetBody', { name: presetDeleteTarget.name })}</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" disabled={presetDeleting} onClick={() => setPresetDeleteTarget(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {t('common.cancel', 'Cancel')}
              </button>
              <button type="button" disabled={presetDeleting} onClick={deletePreset} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {presetDeleting ? t('collections.presetDeleting') : t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {detailsCustomer && (() => {
        const fullCustomer = allCustomers.find(c => c.customer_id === detailsCustomer.customer_id);
        return (
          <CustomerDetailsModal
            customer={fullCustomer}
            customerId={detailsCustomer.customer_id}
            displayName={detailsCustomer.customer_name}
            onClose={() => setDetailsCustomer(null)}
            onAllowMessageChange={setCustomerAllowMessage}
            onEdit={fullCustomer ? () => setEditingCustomer(fullCustomer) : undefined}
          />
        );
      })()}

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSave={updateCustomer}
        />
      )}

      {/* Manual Contact Modal */}
      {manualContactTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closeManualContactModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="manual-contact-title"
            data-testid="manual-contact-dialog"
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="manual-contact-title" className="text-lg font-semibold text-gray-900">
              {t('collections.manualContactModalTitle')}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {t('collections.manualContactModalBody', { customer: manualContactTarget.customer_name })}
            </p>
            <label htmlFor="manual-contact-comment" className="mt-5 block text-sm font-medium text-gray-700">
              {t('collections.manualContactComment')}
            </label>
            <textarea
              id="manual-contact-comment"
              data-testid="manual-contact-comment"
              value={manualContactComment}
              onChange={(event) => setManualContactComment(event.target.value)}
              maxLength={500}
              rows={4}
              disabled={manualContactSubmitting}
              placeholder={t('collections.manualContactCommentPlaceholder')}
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
            />
            <p className="mt-1 text-end text-xs text-gray-400">
              {t('collections.manualContactCharacterCount', {
                count: manualContactComment.length,
                max: 500,
              })}
            </p>
            {manualContactError && (
              <p role="alert" className="mt-3 text-sm text-red-600">
                {manualContactError}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeManualContactModal}
                disabled={manualContactSubmitting}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                data-testid="manual-contact-confirm"
                onClick={handleMarkContacted}
                disabled={manualContactSubmitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {manualContactSubmitting
                  ? t('collections.manualContactSaving')
                  : t('collections.manualContactConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {whatsappTarget && (
        <OpenBalanceWhatsAppModal
          customerId={whatsappTarget.customer_id}
          displayName={whatsappTarget.customer_name}
          phone={whatsappTarget.phone}
          fallbackOpenAmount={whatsappTarget.open_balance}
          fallbackAgedAmount={whatsappTarget.aged_debt}
          messagesEnabled={whatsappTarget.allow_message === true}
          onClose={() => {
            setWhatsappTarget(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
